/**
 * HlsMarkers
 *
 *  HlsJs internal marker managment
 *
 */
'use strict';

import Event from './HlsEvents';
import MarkerM from './utils/markerM';
import EventEmitter from 'eventemitter2';
import EventHandlers from './EventHandlers';
import Utils from './HlsUtils';
import { logger } from './hlsjs/utils/logger';

/**
* @access internal
*/
class HlsMarkers {

	constructor( config = {}, di = {}) {
		this._emitter = di.eventEmitter || new EventEmitter();
		this._markers = new MarkerM();
		this._chunkIndex = [];
		this._markerTriggeredCallback = this._markerTriggered.bind(this);
		this._handlers = new EventHandlers();
		this._lastTime = 0;
		this._instanceId = config.instanceId;

		this._handlers.setHlsEventsList(
			this, // obj to bind to
			this._emitter, // emitter to use
			[
				{ event: Event.TIMED_METADATA_UPDATE,				func: this._onTimedMetadataUpdate	},
				{ event: Event.STREAM_TIMEUPDATE,					func: this._onStreamTimeUpdate		},
				{ event: Event.MEDIA_SEEKED,						func: this._onMediaSeeked			},
			]
		).wireHlsEvents();
	}


	dispose() {
		this._handlers.unwireHlsEvents();
		this._handlers.dispose();
		this._handlers = null;
		this._chunkIndex = null;
		this._markers.dispose();
		this._markers = null;
		this._markerTriggeredCallback = null;
		this._emitter = null;
	}

	get emitter() {
		return this._emitter;
	}

	_emit(event, ...args) {
		this._emitter.emit(event, ...args);
	}

	_onTimedMetadataUpdate( data ) {
		this._addHlsMetaMarkers(data);
	}

	_onStreamTimeUpdate( data ) {
		//The time update comes first sometimes... checking for large jumps is a workaround for now
//		console.log(`_onStreamTimeUpdate time: ${data.currentTime} count: ${this._markers.count}`);
		if (Math.abs(data.currentTime - this._lastTime) < 15) {
			this.timeUpdateMeta( data.currentTime );
		}
		else {
			this.seekMarkers( data.currentTime );
		}
	}

	_onMediaSeeked( data ) {
		this.seekMarkers(data.currentTime);
	}

	_markerTriggered( time, data, id ) {
//		console.log(`markerTriggered time: ${time} id: ${id} type: ${data.type} count: ${this._markers.count}`);
		switch( data.type ) {
		case 'meta':
			this._emit( Event.TIMED_METADATA_PTS_REACHED, {
				time,
				id: data.id,
				sample: data.sample,
			});
			break;

		case 'user':
			data.usercallback(time, data.userdata, id);
			break;

		default:
			throw( new Error( `HlsMarkers._markerTriggered: unknown type ${data.type}`));
		}

	}


	//Passes though with no changes
	addMarker(marker) {
		let markerId = this._markers.addMarker(marker.time, marker.data, marker.callback );

		return markerId;
	}


	addTypedMarker(time, callback, type, data) {
		if( !Utils.isFunction(callback) ) {
			throw new Error( 'HlsMarkers.addTypedMarker: callback must be valid' );
		}

		let markerId = this._markers.addMarker(
			time,
			{
				userdata: data,
				usercallback: callback,
				type: type,
			},
			this._markerTriggeredCallback
		);

		return markerId;
	}


	//Adds a custom de-blocker and an optional callback override
	_addHlsMetaMarkers(data) {
		let markerId = 0;

		if (data.samples !== undefined && data.chunkIdx !== undefined) {
			let replacing = false;

			if(this._chunkIndex[data.chunkIdx] !== undefined) {
				replacing = true;
			}

			this._chunkIndex[data.chunkIdx] = {
				variant: data.variant,
				startTime: data.chunkStartTime,
				endTime: data.chunkEndTime,
			};
//			console.log(`addHlsMetaMarkers index: ${data.chunkIdx} MetaChunk index total: ${this._chunkIndex.length}`);

			if (replacing) {
//				console.log(`MetaChunk removing/replacing ${data.chunkStartTime} to ${data.chunkEndTime} before: ${this._markers.count}`);
				this.removeMetaMarkerRange(data.chunkStartTime, data.chunkEndTime);
//				console.log('MetaChunk removing/replacing after: ' + this._markers.count);
			}

			data.samples.forEach( (sample) => {
				let sampleTime = Number(sample.pts);
				if (isNaN(sampleTime)) {
					logger.log('addHlsMetaMarkers; no pts!'); //This should be fixed...
					sampleTime = data.chunkStartTime;
				}

//				console.log(`addHlsMetaMarkers adding time: ${sampleTime}`);

				markerId = this._markers.addMarker(
					sampleTime,	// time
					{
						sample, // data
						type: 'meta',
						id: data.id,
					},
					this._markerTriggeredCallback );
			});
		}

		return markerId;
	}


	removeMarker(markerId) {
//		console.log(`removeMarker markerId: ${markerId}`);
		this._markers.removeMarker( markerId );
	}


	removeMarkerRange(start, end, filter) {
//		console.log(`removeMarkerRange start: ${start}`);
		this._markers.removeMarkerRange(
			{ time:start },
			{ time:end },
			filter
		);
	}


	removeMetaMarkerRange(start, end) {

//		let trimmed = this._markers.count;

//		console.log(`removeMetaMarkerRange start: ${start} BEFORE count: ${this._markers.count}`);
		this._markers.removeMarkerRange(
			{ time:start },
			{ time:end },
			(v) => v.data.type === 'meta'
		);
//		console.log(`removeMetaMarkerRange AFTER count: ${this._markers.count}`);

//		trimmed -= this._markers.count;
//		if (trimmed !== 0) {
//			console.log(`removeMetaMarkerRange trimmed: ${trimmed}`);
//		}
//		if (this._markers.count !== 0) {
//			console.log(`removeMetaMarkerRange  ${this._markers._markers._values[0].time}`);
//		}
	}


	clearAllMarkers() {
//		console.log(`clearAllMarkers count: ${this._markers.count}`);
		this._markers.clearAllMarkers();
	}


	get count() {
		return this._markers.count;
	}


	seekMarkers(time) {
//		console.log(`seekMarkers: time ${time} count ${this._markers.count}` );
		this._markers.seekMarkers(time);
		this._lastTime = time;
	}


	timeUpdate(timeNow) {
		this._markers.timeUpdate(timeNow);
	}


	timeUpdateMeta(timeNow) {
//		console.log(`timeUpdateMeta: time ${timeNow} count ${this._markers.count}` );

//		This is how you do a one-shot metadata... but we do not want it right now
//		this._markers.timeUpdateWithExtract(timeNow, (v) => v.data.type === 'meta');

		this._markers.timeUpdate(timeNow);
		this._markers.seekMarkers(timeNow);
		this._lastTime = timeNow;
	}


	//Used to prevent Metadata building up. Intended to be called when the buffered range changes.
	trimMeta(upTo) {
//		console.log(`trimMeta upTo: ${upTo}`);
		if (upTo > 1) {
			//remove is inclusive. It's safer to remove a bit less
			this.removeMetaMarkerRange(0, upTo - 0.5);
		}

	}

}
export default HlsMarkers;



// WEBPACK FOOTER //
// ./src/HlsMarkers.js